/**
 * Raid Start View
 *
 * Entry point for the raid system. Allows players to:
 * - Create a new raid room (navigates to raid type selection)
 * - Join an existing raid room via invite code
 *
 * Design: ZZZ / Persona 5 inspired - bold colors, sharp angles, high contrast
 */

import { useEffect, useState } from "react";
import { useSPAContext } from "@/providers/spa-provider";
import { useAuth } from "@/providers/auth-provider";
import { useRaid } from "@/providers/raid-provider";
import { RouteNames } from "@/types/route-names";
import { Button } from "@/components/ui/buttons/button";
import { useUser } from "@/providers/user-provider";
import { PokemonCampaignTeamDisplay } from "@/components/pokemon/pokemon-campaign-team-display";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { quitRaid } from "@/services/raid/quit-duel";

export const RaidStartView = () => {
  const { setCurrentRoute, setPreviousRoute } = useSPAContext();
  const { getAccessToken, state: authState } = useAuth();
  const { raidState } = useRaid();
  const { joinRaidLobby } = useRaid();
  const { state } = useUser();

  const [inviteCode, setInviteCode] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnectingToRaid, setIsConnectingToRaid] = useState(false);
  const [showQuitButton, setShowQuitButton] = useState(false);

  /**
   * Watch for successful connection and route to lobby
   */
  useEffect(() => {
    if (isConnectingToRaid && raidState.connection_status === "connected") {
      setIsConnectingToRaid(false);
      setCurrentRoute(RouteNames.RAID_LOBBY);
    }
  }, [isConnectingToRaid, raidState.connection_status, setCurrentRoute]);

  /**
   * Watch for connection errors while in connecting state
   */
  useEffect(() => {
    if (isConnectingToRaid && raidState.status === "error") {
      setIsConnectingToRaid(false);
      setShowQuitButton(false);
      setError(raidState.error?.message || "Failed to connect to raid");
    }
  }, [isConnectingToRaid, raidState.status, raidState.error]);

  /**
   * Show quit button after 10 seconds of loading
   */
  useEffect(() => {
    if (!isConnectingToRaid) return;

    const timer = setTimeout(() => {
      setShowQuitButton(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [isConnectingToRaid]);

  const handleCreateRoom = () => {
    setCurrentRoute(RouteNames.RAID_SELECT);
  };

  const handleQuitRaid = async () => {
    const token = getAccessToken();
    if (!token || authState.status !== "authenticated") return;

    const playerId = authState.user?.telegram_id;
    if (!playerId) return;

    try {
      // Find current raid ID from context or state
      if (raidState.raid_id) {
        console.log(
          "[RaidStartView] Attempting to quit raid:",
          raidState.raid_id,
        );
        await quitRaid(token, raidState.raid_id);
        console.log("[RaidStartView] Successfully quit raid");
      }
    } catch (error) {
      console.error("[RaidStartView] Failed to quit raid:", error);
    } finally {
      setIsConnectingToRaid(false);
      setShowQuitButton(false);
      setError("Raid connection timed out - please try again");
    }
  };

  const handleJoinRoom = async () => {
    if (!inviteCode.trim()) {
      setError("Please enter an invite code");
      return;
    }

    const token = getAccessToken();
    if (!token || authState.status !== "authenticated") {
      setError("Authentication required");
      return;
    }

    const playerId = authState.user?.telegram_id;
    if (!playerId) {
      setError("Player ID not found");
      return;
    }

    setIsJoining(true);
    setError(null);

    try {
      const result = await joinRaidLobby(
        token,
        playerId,
        inviteCode.trim().toUpperCase(),
      );

      if (result.success) {
        // Start watching for connection; blocking overlay will show
        setIsConnectingToRaid(true);
      } else {
        setError(result.error || "Failed to join raid");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error("[RaidStartView] Join error:", err);
    } finally {
      setIsJoining(false);
    }
  };

  if (state.status !== "loaded")
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-screen w-full relative overflow-hidden bg-pk-blue-3 pb-32">
        {/* Dot pattern overlay - large dots fading out toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 7px, transparent 7px)",
            backgroundSize: "14px 14px",
            opacity: 0.15,
            maskImage: "linear-gradient(to bottom, black 0%, transparent 30%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, transparent 60%)",
          }}
        />
        {/* Dot pattern overlay - small dots fading in toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 1.5px, transparent 1.5px)",
            backgroundSize: "4px 4px",
            opacity: 0.08,
            maskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
          }}
        />

        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-pk-yellow-2 border-t-transparent"></div>
          <span className="text-pk-white font-semibold text-lg mt-4">
            Loading...
          </span>
        </div>
      </div>
    );

  return (
    <div className="flex flex-col h-full min-h-screen w-full relative overflow-hidden bg-pk-blue-3 pb-32">
      {/* Blocking overlay while connecting to raid */}
      {isConnectingToRaid && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center pointer-events-auto">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-pk-yellow-2 border-t-transparent"></div>
            <span className="text-pk-white font-semibold text-lg">
              Connecting to raid...
            </span>
            {showQuitButton && (
              <SimpleButton variant="beige" onClick={handleQuitRaid}>
                Quit Raid
              </SimpleButton>
            )}
          </div>
        </div>
      )}
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Dot pattern overlay - large dots fading out toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 7px, transparent 7px)",
            backgroundSize: "14px 14px",
            opacity: 0.15,
            maskImage: "linear-gradient(to bottom, black 0%, transparent 30%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, transparent 60%)",
          }}
        />
        {/* Dot pattern overlay - small dots fading in toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 1.5px, transparent 1.5px)",
            backgroundSize: "4px 4px",
            opacity: 0.08,
            maskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
          }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between ">
        <div className="flex flex-col  ml-4 p-2">
          {/* Top bar with tags */}
          <div className="flex items-center justify-between mb-2 ">
            <div className="flex items-center gap-2">
              <span className="bg-pk-psychic text-white text-[10px] font-black uppercase px-2 py-1 tracking-wider">
                CO-OP
              </span>
              <span className="bg-pk-gray-2 text-pk-gray-4 text-[10px] font-bold uppercase px-2 py-1">
                1-4 PLAYERS
              </span>
            </div>
          </div>

          {/* Title */}
          <div className="relative ">
            <h1 className="text-4xl xs:text-6xl font-black text-white uppercase tracking-tight leading-none drop-shadow-lg">
              RAIDS
            </h1>
          </div>
        </div>
      </div>

      <div className="flex flex-col  mx-4 mt-0 xs:mt-4 z-10">
        <div className="flex items-center justify-between gap-4  ">
          <h2 className="text-pk-gray-4 h-full items-center flex rounded-tr-lg font-bold uppercase text-lg px-2 bg-pk-blue-1">
            Main Team
          </h2>
          <div className="mb-1">
            <SimpleButton
              variant="beige"
              onClick={() => {
                setPreviousRoute(RouteNames.RAID_START);
                setCurrentRoute(RouteNames.TEAM_LIST);
              }}
            >
              Change Team
            </SimpleButton>
          </div>
        </div>
        <div className="p-2 rounded-lg rounded-tl-none bg-pk-blue-1">
          <PokemonCampaignTeamDisplay
            pokemons={state.profile.main_team}
            className="pointer-events-none"
          />
        </div>
      </div>

      {/* Main Actions */}
      <div className=" flex flex-col gap-1 px-4 pt-2 relative z-10  my-auto w-full">
        <Button variant="beige" onClick={handleCreateRoom}>
          <span className="text-lg font-bold">Create Room</span>
        </Button>

        <div className="flex items-center justify-center w-full">
          <span>or</span>
        </div>

        <div className="flex flex-col">
          {/* Input row */}
          <div className="flex gap-2 relative  bg-pk-gray-1">
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value.toUpperCase());
                setError(null);
              }}
              placeholder="Enter join code"
              maxLength={8}
              className="pr-8 flex-1 text-white text-lg font-mono font-bold px-2 py-1 xs:py-2.5 border-2 border-pk-gray-2 focus:outline-none focus:border-pk-blue-4 placeholder:text-pk-gray-3 placeholder:text-sm uppercase tracking-wider"
            />

            <div className="absolute right-0 z-12">
              <Button
                variant="beige"
                disabled={isJoining || !inviteCode.trim()}
                onClick={handleJoinRoom}
                className="z-12 !opacity-100"
              >
                {isJoining ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-pk-blue-3 border-t-transparent"></div>
                    <span className="text-lg font-bold z-12">Joining...</span>
                  </div>
                ) : (
                  <span className="text-lg font-bold z-12">Ok</span>
                )}
              </Button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-2 px-3 py-2 bg-pk-red-1 border border-pk-red-2 rounded text-pk-white text-sm font-semibold">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
